// frontend-react/src/services/projectionProvenance.js
//
// Derive the offseason projection label FROM THE RESPONSE, never from league config.
//
// WHY (Bible §28.7). KeeperSelection previously labelled its projections view
// `{semanticSeasons.projected_season} Projections` — a value read from league CONFIG, entirely
// independent of what the payload contained. So it asserted "2027 Projections" while showing
// 2026 current-season numbers, and would keep asserting it after real 2027 snapshots landed,
// leaving a user no way to distinguish the two states.
//
// The backend already stamps, per player, on the `projected` block:
//    projection_source         'preseason' | 'v3_serving' | 'current_season'
//    projection_source_season  the season the numbers ACTUALLY describe
//    display_note              the honest caption, when the source is a degradation
//
// This module reduces those per-player stamps to ONE screen-level statement, and refuses to
// invent one when the payload has not arrived (Rule 9 — the caller shows a loading state, never
// a guessed year).
//
// MIXED PAYLOADS ARE REAL. In October a snapshot may cover only qualified players, so one roster
// can legitimately carry both `preseason` (2027) and `current_season` (2026) rows. We say so
// rather than picking the majority and hiding the rest.

/** Human label per source. `current_season` is deliberately NOT called a projection. */
export const SOURCE_LABEL = {
  preseason: (season) => `${season} Projections`,
  v3_serving: (season) => `${season} Projections`,
  current_season: (season) => `${season} Season-to-Date`,
};

/**
 * @param {Array} players roster/eligible entries, each optionally carrying `.projected`
 * @returns {null|{season, source, note, count, total, mixed, breakdown}}
 *          null when NOTHING in the payload carries provenance (not loaded / older API).
 */
export function deriveProjectionProvenance(players) {
  const list = Array.isArray(players) ? players : [];
  const buckets = new Map();
  let stamped = 0;

  for (const p of list) {
    const blk = p && p.projected;
    if (!blk) continue;
    const season = blk.projection_source_season;
    const source = blk.projection_source;
    if (season == null || !source) continue;
    stamped += 1;
    const key = `${source}:${season}`;
    const cur = buckets.get(key) || {
      season, source, note: blk.display_note || null, count: 0,
    };
    cur.count += 1;
    if (!cur.note && blk.display_note) cur.note = blk.display_note;
    buckets.set(key, cur);
  }

  if (stamped === 0) return null;   // caller falls back to a LOADING state, never a config year

  const breakdown = [...buckets.values()].sort((a, b) => b.count - a.count);
  const primary = breakdown[0];
  return {
    season: primary.season,
    source: primary.source,
    note: primary.note,
    count: primary.count,
    total: stamped,
    mixed: breakdown.length > 1,
    breakdown,
  };
}

/** The toggle/heading label. Never returns a config-derived year. */
export function projectionLabel(prov) {
  if (!prov) return null;
  const fmt = SOURCE_LABEL[prov.source] || ((s) => `${s} Projections`);
  return fmt(prov.season);
}

/**
 * The caption rendered beneath the label. Prefers the backend's own `display_note`; adds a
 * mixed-payload sentence when the roster spans more than one source, because a single label
 * cannot be true for every row in that case.
 */
export function projectionCaption(prov) {
  if (!prov) return null;
  const parts = [];
  if (prov.note) parts.push(prov.note);
  if (prov.mixed) {
    const others = prov.breakdown.slice(1)
      .map((b) => `${b.count} ${SOURCE_LABEL[b.source] ? SOURCE_LABEL[b.source](b.season) : b.season}`)
      .join(', ');
    parts.push(`Mixed sources: ${prov.count} of ${prov.total} players ${
      SOURCE_LABEL[prov.source] ? SOURCE_LABEL[prov.source](prov.season) : prov.season}; ${others}.`);
  }
  return parts.length ? parts.join(' · ') : null;
}
